import { apiService } from "./api.service";

class CategoryService {
  // ============ PRIVATE HELPER METHODS ============

  /**
   * Get authentication token from cookie
   * @returns {string|null} Token if found, null otherwise
   */
  getAuthTokenFromCookie() {
    try {
      const cookies = document.cookie.split(";");
      for (let cookie of cookies) {
        const [name, value] = cookie.trim().split("=");
        if (name === "admin_access_token") {
          return value;
        }
      }
    } catch (error) {
      console.warn("Error reading auth token from cookie:", error);
    }
    return null;
  }

  /**
   * Check if user is authenticated
   * @returns {boolean} True if authenticated
   */
  isAuthenticated() {
    const token = this.getAuthTokenFromCookie();
    const userStr = localStorage.getItem("admin_user");
    return !!(token && userStr);
  }

  /**
   * Ensure authentication before making request
   * @throws {Error} If not authenticated
   */
  ensureAuthenticated() {
    if (!this.isAuthenticated()) {
      throw new Error("Not authenticated. Please login first.");
    }
  }

  /**
   * Get request config with proper authentication
   * @param {Object} customConfig - Custom configuration
   * @returns {Object} Request configuration
   */
  getRequestConfig(customConfig = {}) {
    const token = this.getAuthTokenFromCookie();
    const config = {
      withCredentials: true,
      ...customConfig,
    };

    // Add Authorization header if token exists
    if (token) {
      config.headers = {
        ...config.headers,
        Authorization: `Bearer ${token}`,
      };
    }

    return config;
  }

  /**
   * Handle API errors and retry logic
   * @param {Function} requestFn - The API request function
   * @param {number} retries - Number of retry attempts
   * @returns {Promise<any>} API response
   */
  async withRetry(requestFn, retries = 3) {
    for (let i = 0; i < retries; i++) {
      try {
        return await requestFn();
      } catch (error) {
        // If it's the last retry or not a retryable error, throw
        if (
          i === retries - 1 ||
          error?.response?.status === 401 ||
          error?.response?.status === 403
        ) {
          throw error;
        }

        // Wait before retrying (exponential backoff)
        await new Promise((resolve) =>
          setTimeout(resolve, Math.pow(2, i) * 1000),
        );
      }
    }
  }

  /**
   * Validate category data before sending to API
   * @param {Object} data - Category data
   * @returns {Object} Validation result
   */
  validateCategoryData(data) {
    const errors = [];

    if (!data.name || data.name.trim().length < 2) {
      errors.push("Category name must be at least 2 characters");
    }

    if (!data.slug || data.slug.trim().length < 2) {
      errors.push("Category slug must be at least 2 characters");
    }

    if (data.display_order && isNaN(parseInt(data.display_order))) {
      errors.push("Display order must be a number");
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  }

  /**
   * Format category data for API
   * @param {Object} uiData - UI category data
   * @returns {Object} API-ready category data
   */
  formatForAPI(uiData) {
    return {
      name: uiData.name?.trim() || "",
      slug: uiData.slug?.trim() || "",
      description: uiData.description?.trim() || "",
      parent_id: uiData.parent_id || null,
      image: uiData.image,
      icon: uiData.icon || null,
      is_active: Boolean(uiData.is_active),
      is_featured: Boolean(uiData.is_featured),
      display_order: parseInt(uiData.display_order) || 0,
      meta_title: uiData.meta_title?.trim() || "",
      meta_description: uiData.meta_description?.trim() || "",
      meta_keywords: uiData.meta_keywords?.trim() || "",
    };
  }

  /**
   * Format API response for UI display
   * @param {Object} apiData - API category data
   * @returns {Object} UI-ready category data
   */
  formatForUI(apiData) {
    // Handle image buffer conversion
    let image = apiData.image;
    if (image && image.type === "Buffer" && Array.isArray(image.data)) {
      image = image;
    }

    return {
      id: apiData.id,
      name: apiData.name,
      slug: apiData.slug,
      description: apiData.description || "",
      parent_id: apiData.parent_id,
      parent_name: apiData.parent_name,
      image: image,
      icon: apiData.icon,
      is_active: Boolean(apiData.is_active),
      is_featured: Boolean(apiData.is_featured),
      display_order: apiData.display_order || 0,
      product_count: apiData.product_count || 0,
      children_count: apiData.children_count || 0,
      depth: apiData.depth || 0,
      path: apiData.path || "",
      meta_title: apiData.meta_title || "",
      meta_description: apiData.meta_description || "",
      meta_keywords: apiData.meta_keywords || "",
      created_at: apiData.created_at,
      updated_at: apiData.updated_at,
      children: apiData.children
        ? apiData.children.map(this.formatForUI.bind(this))
        : [],
    };
  }

  // ============ PUBLIC ENDPOINTS ============

  async getCategories(params = {}) {
    try {
      const config = this.getRequestConfig({ params });
      const response = await this.withRetry(() =>
        apiService.get("/categories/categories", config),
      );

      // Format the response data for UI
      if (response.data?.data) {
        response.data.data = response.data.data.map(
          this.formatForUI.bind(this),
        );
      } else if (Array.isArray(response.data)) {
        response.data = response.data.map(this.formatForUI.bind(this));
      }

      return response;
    } catch (error) {
      return {
        success: false,
        error: "Failed to fetch categories",
        message: error.message,
        status: error.response?.status,
      };
    }
  }

  async getCategoryTree() {
    try {
      const config = this.getRequestConfig();
      const response = await this.withRetry(() =>
        apiService.get("/categories/categories/tree", config),
      );
      return response;
    } catch (error) {
      return {
        success: false,
        error: "Failed to fetch category tree",
        message: error.message,
      };
    }
  }

  async getRootCategories() {
    try {
      const config = this.getRequestConfig();
      const response = await this.withRetry(() =>
        apiService.get("/categories/categories/root", config),
      );
      return response;
    } catch (error) {
      return {
        success: false,
        error: "Failed to fetch root categories",
        message: error.message,
      };
    }
  }

  async getActiveCategories() {
    try {
      const config = this.getRequestConfig();
      const response = await this.withRetry(() =>
        apiService.get("/categories/categories/active", config),
      );
      return response;
    } catch (error) {
      return {
        success: false,
        error: "Failed to fetch active categories",
        message: error.message,
      };
    }
  }

  async getActiveCategoriesWithProducts() {
    try {
      const config = this.getRequestConfig();
      const response = await this.withRetry(() =>
        apiService.get("/categories/categories/active-with-products", config),
      );
      return response;
    } catch (error) {
      return {
        success: false,
        error: "Failed to fetch active categories with products",
        message: error.message,
      };
    }
  }

  async getFeaturedCategoriesWithProducts() {
    try {
      const config = this.getRequestConfig();
      const response = await this.withRetry(() =>
        apiService.get("/categories/categories/featured-with-products", config),
      );
      return response;
    } catch (error) {
      return {
        success: false,
        error: "Failed to fetch featured categories with products",
        message: error.message,
      };
    }
  }

  async searchCategories(query, params = {}) {
    try {
      const config = this.getRequestConfig({ params: { q: query, ...params } });
      const response = await this.withRetry(() =>
        apiService.get("/categories/categories/search", config),
      );
      return response;
    } catch (error) {
      return {
        success: false,
        error: "Failed to search categories",
        message: error.message,
      };
    }
  }

  async getCategory(idOrSlug) {
    try {
      const config = this.getRequestConfig();
      const response = await this.withRetry(() =>
        apiService.get(`/categories/categories/${idOrSlug}`, config),
      );

      if (response.data?.data) {
        response.data.data = this.formatForUI(response.data.data);
      }

      return response;
    } catch (error) {
      return {
        success: false,
        error: "Failed to fetch category",
        message: error.message,
      };
    }
  }

  async getCategoryBySlug(slug) {
    try {
      const config = this.getRequestConfig();
      const response = await this.withRetry(() =>
        apiService.get(`/categories/categories/slug/${slug}`, config),
      );
      return response;
    } catch (error) {
      return {
        success: false,
        error: "Failed to fetch category by slug",
        message: error.message,
      };
    }
  }

  async getSubcategories(parentId, params = {}) {
    try {
      const config = this.getRequestConfig({ params });
      const response = await this.withRetry(() =>
        apiService.get(
          `/categories/categories/${parentId}/subcategories`,
          config,
        ),
      );
      return response;
    } catch (error) {
      return {
        success: false,
        error: "Failed to fetch subcategories",
        message: error.message,
      };
    }
  }

  async getCategoryPath(id) {
    try {
      const config = this.getRequestConfig();
      const response = await this.withRetry(() =>
        apiService.get(`/categories/categories/${id}/path`, config),
      );
      return response;
    } catch (error) {
      return {
        success: false,
        error: "Failed to fetch category path",
        message: error.message,
      };
    }
  }

  // ============ ADMIN ENDPOINTS ============

  async createCategory(data) {
    try {
      this.ensureAuthenticated();

      if (data instanceof FormData) {
        // Convert FormData to JSON object
        const jsonData = {};
        for (let [key, value] of data.entries()) {
          // Skip image file - should be handled separately
          if (key === "image" && value instanceof File) {
            continue;
          }

          // Convert boolean strings to actual booleans
          if (value === "true") jsonData[key] = true;
          else if (value === "false") jsonData[key] = false;
          // Handle empty parent_id
          else if (key === "parent_id" && value === "") jsonData[key] = null;
          // Handle display_order as number
          else if (key === "display_order")
            jsonData[key] = parseInt(value) || 0;
          // Handle other fields
          else jsonData[key] = value;
        }

        data = jsonData;
      }

      // Validate required fields
      if (!data.name || !data.name.trim()) {
        return {
          success: false,
          error: "Validation failed",
          message: "Name is required",
        };
      }

      if (!data.slug || !data.slug.trim()) {
        return {
          success: false,
          error: "Validation failed",
          message: "Slug is required",
        };
      }

      // Format the data for API
      const apiData = {
        name: data.name.trim(),
        slug: data.slug.trim(),
        description: data.description?.trim() || null,
        parent_id: data.parent_id || null,
        icon: data.icon || null,
        is_active: data.is_active === true || data.is_active === "true",
        is_featured: data.is_featured === true || data.is_featured === "true",
        display_order: parseInt(data.display_order) || 0,
        meta_title: data.meta_title?.trim() || null,
        meta_description: data.meta_description?.trim() || null,
        meta_keywords: data.meta_keywords?.trim() || null,
      };

      const config = this.getRequestConfig({
        headers: {
          "Content-Type": "application/json",
        },
      });

      const response = await this.withRetry(() =>
        apiService.post("/categories/categories", apiData, config),
      );

      return response;
    } catch (error) {
      return {
        success: false,
        error: "Failed to create category",
        message: error.response?.data?.message || error.message,
        status: error.response?.status,
        data: error.response?.data,
      };
    }
  }

  async updateCategory(id, formData) {
    try {
      this.ensureAuthenticated();

      // If formData is FormData, use it directly
      if (formData instanceof FormData) {
        const config = this.getRequestConfig({
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });

        const response = await this.withRetry(() =>
          apiService.put(`/categories/categories/${id}`, formData, config),
        );

        return response;
      }

      // Otherwise handle as regular object
      const validation = this.validateCategoryData(formData);
      if (!validation.isValid) {
        return {
          success: false,
          error: "Validation failed",
          message: validation.errors.join(", "),
          errors: validation.errors,
        };
      }

      const apiData = this.formatForAPI(formData);
      const config = this.getRequestConfig();

      const response = await this.withRetry(() =>
        apiService.put(`/categories/categories/${id}`, apiData, config),
      );

      if (response.data?.data) {
        response.data.data = this.formatForUI(response.data.data);
      }

      return response;
    } catch (error) {
      return {
        success: false,
        error: "Failed to update category",
        message: error.message,
        status: error.response?.status,
      };
    }
  }

  // Add method to upload category image
  async uploadCategoryImage(categoryId, file) {
    try {
      this.ensureAuthenticated();

      const formData = new FormData();
      formData.append("image", file);

      const config = this.getRequestConfig({
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      const response = await this.withRetry(() =>
        apiService.post(
          `/categories/categories/${categoryId}/images`,
          formData,
          config,
        ),
      );

      return response;
    } catch (error) {
      return {
        success: false,
        error: "Failed to upload category image",
        message: error.message,
      };
    }
  }

  async deleteCategory(id) {
    try {
      this.ensureAuthenticated();
      const config = this.getRequestConfig();
      const response = await this.withRetry(() =>
        apiService.delete(`/categories/categories/${id}`, config),
      );
      return response;
    } catch (error) {
      return {
        success: false,
        error: "Failed to delete category",
        message: error.message,
        status: error.response?.status,
      };
    }
  }

  // ============ IMAGE MANAGEMENT ============

  async uploadCategoryImage(categoryId, file) {
    try {
      this.ensureAuthenticated();

      const formData = new FormData();
      formData.append("image", file);

      const config = this.getRequestConfig({
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      const response = await this.withRetry(() =>
        apiService.post(
          `/categories/categories/${categoryId}/images`,
          formData,
          config,
        ),
      );

      return response;
    } catch (error) {
      return {
        success: false,
        error: "Failed to upload category image",
        message: error.message,
      };
    }
  }

  async updateCategoryImage(imageId, file) {
    try {
      this.ensureAuthenticated();

      const formData = new FormData();
      formData.append("image", file);

      const config = this.getRequestConfig({
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      const response = await this.withRetry(() =>
        apiService.put(
          `/categories/categories/images/${imageId}`,
          formData,
          config,
        ),
      );

      return response;
    } catch (error) {
      return {
        success: false,
        error: "Failed to update category image",
        message: error.message,
      };
    }
  }

  async deleteCategoryImage(imageId) {
    try {
      this.ensureAuthenticated();
      const config = this.getRequestConfig();
      const response = await this.withRetry(() =>
        apiService.delete(`/categories/categories/images/${imageId}`, config),
      );
      return response;
    } catch (error) {
      return {
        success: false,
        error: "Failed to delete category image",
        message: error.message,
      };
    }
  }

  async getCategoryImage(imageId) {
    try {
      const config = this.getRequestConfig();
      const response = await this.withRetry(() =>
        apiService.get(`/categories/categories/images/${imageId}`, config),
      );
      return response;
    } catch (error) {
      return {
        success: false,
        error: "Failed to fetch category image",
        message: error.message,
      };
    }
  }

  async getCategoryImageData(imageId) {
    try {
      const config = this.getRequestConfig({
        responseType: "blob",
      });
      const response = await this.withRetry(() =>
        apiService.get(`/categories/categories/images/${imageId}/data`, config),
      );
      return response;
    } catch (error) {
      return {
        success: false,
        error: "Failed to fetch category image data",
        message: error.message,
      };
    }
  }

  async getCategoryImageThumbnail(imageId) {
    try {
      const config = this.getRequestConfig({
        responseType: "blob",
      });
      const response = await this.withRetry(() =>
        apiService.get(
          `/categories/categories/images/${imageId}/thumbnail`,
          config,
        ),
      );
      return response;
    } catch (error) {
      return {
        success: false,
        error: "Failed to fetch category image thumbnail",
        message: error.message,
      };
    }
  }

  // ============ ICON MANAGEMENT ============

  async uploadCategoryIcon(categoryId, file) {
    try {
      this.ensureAuthenticated();

      const formData = new FormData();
      formData.append("icon", file);

      const config = this.getRequestConfig({
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      const response = await this.withRetry(() =>
        apiService.post(
          `/categories/categories/${categoryId}/icon`,
          formData,
          config,
        ),
      );

      return response;
    } catch (error) {
      return {
        success: false,
        error: "Failed to upload category icon",
        message: error.message,
      };
    }
  }

  async deleteCategoryIcon(categoryId) {
    try {
      this.ensureAuthenticated();
      const config = this.getRequestConfig();
      const response = await this.withRetry(() =>
        apiService.delete(`/categories/categories/${categoryId}/icon`, config),
      );
      return response;
    } catch (error) {
      return {
        success: false,
        error: "Failed to delete category icon",
        message: error.message,
      };
    }
  }

  // ============ HIERARCHY OPERATIONS ============

  async moveCategory(id, data) {
    try {
      this.ensureAuthenticated();
      const config = this.getRequestConfig();
      const response = await this.withRetry(() =>
        apiService.put(`/categories/categories/${id}/move`, data, config),
      );
      return response;
    } catch (error) {
      return {
        success: false,
        error: "Failed to move category",
        message: error.message,
      };
    }
  }

  async toggleActiveStatus(id) {
    try {
      this.ensureAuthenticated();
      const config = this.getRequestConfig();
      const response = await this.withRetry(() =>
        apiService.put(
          `/categories/categories/${id}/toggle-active`,
          {},
          config,
        ),
      );
      return response;
    } catch (error) {
      return {
        success: false,
        error: "Failed to toggle active status",
        message: error.message,
      };
    }
  }

  async toggleFeaturedStatus(id) {
    try {
      this.ensureAuthenticated();
      const config = this.getRequestConfig();
      const response = await this.withRetry(() =>
        apiService.put(
          `/categories/categories/${id}/toggle-featured`,
          {},
          config,
        ),
      );
      return response;
    } catch (error) {
      return {
        success: false,
        error: "Failed to toggle featured status",
        message: error.message,
      };
    }
  }

  async reorderCategories(orderData) {
    try {
      this.ensureAuthenticated();
      const config = this.getRequestConfig();
      const response = await this.withRetry(() =>
        apiService.post("/categories/categories/reorder", orderData, config),
      );
      return response;
    } catch (error) {
      return {
        success: false,
        error: "Failed to reorder categories",
        message: error.message,
      };
    }
  }

  // ============ MAINTENANCE ============

  async updateProductCount(id) {
    try {
      this.ensureAuthenticated();
      const config = this.getRequestConfig();
      const response = await this.withRetry(() =>
        apiService.post(
          `/categories/categories/${id}/update-product-count`,
          {},
          config,
        ),
      );
      return response;
    } catch (error) {
      return {
        success: false,
        error: "Failed to update product count",
        message: error.message,
      };
    }
  }

  // ============ STATISTICS ============

  async getCategoryStats() {
    try {
      this.ensureAuthenticated();
      const config = this.getRequestConfig();
      const response = await this.withRetry(() =>
        apiService.get("/categories/categories/stats", config),
      );
      return response;
    } catch (error) {
      return {
        success: false,
        error: "Failed to fetch category statistics",
        message: error.message,
      };
    }
  }

  // ============ BATCH OPERATIONS ============

  async batchDeleteCategories(ids) {
    try {
      this.ensureAuthenticated();
      const config = this.getRequestConfig();
      const response = await this.withRetry(() =>
        apiService.post("/categories/categories/batch/delete", { ids }, config),
      );
      return response;
    } catch (error) {
      return {
        success: false,
        error: "Failed to batch delete categories",
        message: error.message,
      };
    }
  }

  async batchActivateCategories(ids) {
    try {
      this.ensureAuthenticated();
      const config = this.getRequestConfig();
      const response = await this.withRetry(() =>
        apiService.post(
          "/categories/categories/batch/activate",
          { ids },
          config,
        ),
      );
      return response;
    } catch (error) {
      return {
        success: false,
        error: "Failed to batch activate categories",
        message: error.message,
      };
    }
  }

  async batchDeactivateCategories(ids) {
    try {
      this.ensureAuthenticated();
      const config = this.getRequestConfig();
      const response = await this.withRetry(() =>
        apiService.post(
          "/categories/categories/batch/deactivate",
          { ids },
          config,
        ),
      );
      return response;
    } catch (error) {
      return {
        success: false,
        error: "Failed to batch deactivate categories",
        message: error.message,
      };
    }
  }

  async batchUpdateFeatured(ids, featured) {
    try {
      this.ensureAuthenticated();
      const config = this.getRequestConfig();
      const response = await this.withRetry(() =>
        apiService.post(
          "/categories/categories/batch/featured",
          { ids, featured },
          config,
        ),
      );
      return response;
    } catch (error) {
      return {
        success: false,
        error: "Failed to batch update featured status",
        message: error.message,
      };
    }
  }

  // ============ IMPORT/EXPORT ============

  async importCategories(file) {
    try {
      this.ensureAuthenticated();

      const formData = new FormData();
      formData.append("file", file);

      const config = this.getRequestConfig({
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      const response = await this.withRetry(() =>
        apiService.post("/categories/categories/import", formData, config),
      );

      return response;
    } catch (error) {
      return {
        success: false,
        error: "Failed to import categories",
        message: error.message,
      };
    }
  }

  async exportCategories(format = "json") {
    try {
      this.ensureAuthenticated();
      const config = this.getRequestConfig({
        responseType: "blob",
      });
      const response = await this.withRetry(() =>
        apiService.get(
          `/categories/categories/export?format=${format}`,
          config,
        ),
      );
      return response;
    } catch (error) {
      return {
        success: false,
        error: "Failed to export categories",
        message: error.message,
      };
    }
  }
}

export const categoryService = new CategoryService();
